
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authManager } from '../utils/auth';
import { isNonMmlAdmin } from '../utils/roles';

const Sidebar = () => {
  const location = useLocation();
  const { pathname } = location;
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    authManager.validateUser().then(setCurrentUser).catch(() => setCurrentUser(null));
  }, []);

  const showSettings = isNonMmlAdmin(currentUser);

  const ccaRoutes = [
    '/analytics',
    '/cca',
    '/nextTrip',
    '/vausa',
    '/denali',
    '/insider',
    '/insurance',
    '/lifestyle',
    '/voice'
  ];

  const isCCAPath = ccaRoutes.some(route => pathname.startsWith(route));

  const isMMIPath =
    pathname.startsWith('/brands') ||
    pathname.startsWith('/brandsedit') ||
    pathname.startsWith('/products') ||
    pathname.startsWith('/productsedit') ||
    (pathname.startsWith('/category') && pathname !== '/categoryMapping') ||
    pathname.startsWith('/categoryAdd') ||
    pathname.startsWith('/categoryView') ||
    pathname === '/mmi-dashboard' ||
    pathname === '/categoryMapping';

  const [ccaOpen, setCcaOpen] = useState(isCCAPath);
  const [mmiOpen, setMmiOpen] = useState(isMMIPath);
  const [membershipOpen, setMembershipOpen] = useState(false);
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizSubOpen, setQuizSubOpen] = useState(false);

  const membershipRoutes = [
    '/serviceBenefits'
  ];
  const isMembershipPath = membershipRoutes.some(route => pathname.startsWith(route));

  const settingsRoutes = [
    '/programFlags',
    '/dropdownOptions',
    '/oidcClients',
    '/partnerInquiries'
  ];
  const isSettingsPath = settingsRoutes.some(route => pathname.startsWith(route));

  const quizRoutes = [
    '/quizQuestions',
    '/quizReportRules',
    '/quizLeads',
    '/vaBenefits'
  ];
  const isQuizPath = quizRoutes.some(route => pathname.startsWith(route));

  const loyaltyRoutes = [
    '/loyaltyReceipts',
    '/loyaltyInsights',
    '/loyaltyRedemptions',
    '/loyaltyUnlocks',
    '/loyaltyRules'
  ];
  const isLoyaltyPath = loyaltyRoutes.some(route => pathname.startsWith(route));

  useEffect(() => {
    if (isCCAPath) {
      setCcaOpen(true);
    }
    if (isMMIPath) {
      setMmiOpen(true);
    }
    if (isMembershipPath) {
      setMembershipOpen(true);
    }
    if (isLoyaltyPath) {
      setLoyaltyOpen(true);
    }
    if (isSettingsPath) {
      setSettingsOpen(true);
    }
    if (isQuizPath) {
      setQuizOpen(true);
      setQuizSubOpen(true);
    }
  }, [pathname, isCCAPath, isMMIPath, isMembershipPath, isLoyaltyPath, isSettingsPath, isQuizPath]);

  const toggleCCA = () => {
    setCcaOpen(prev => !prev);
  };

  const toggleMMI = () => {
    setMmiOpen(prev => !prev);
  };
  const toggleMembership = () => {
    setMembershipOpen(prev => !prev);
  };

  const toggleLoyalty = () => {
    setLoyaltyOpen(prev => !prev);
  };

  const toggleSettings = () => {
    setSettingsOpen(prev => !prev);
  };

  const toggleQuiz = () => {
    setQuizOpen(prev => !prev);
  };

  const toggleQuizSub = () => {
    setQuizSubOpen(prev => !prev);
  };

  return (
    <aside className="main-sidebar sidebar-dark-primary elevation-4">
      <div className="brand-link logo-switch">
        <img src="/assets/img/logo_sm.png" alt="GeoHunt360" className="brand-image-xl logo-xs" />
        <img src="/assets/img/logo.png" alt="GeoHunt360" className="brand-image-xs logo-xl" />
      </div>

      <div className="sidebar">
        <nav className="mt-2">
          <ul className="nav nav-pills nav-sidebar flex-column" data-widget="treeview" role="menu" data-accordion="false">

            <li className="nav-item">
              <Link to="/dashboard" className={`nav-link ${pathname === '/dashboard' ? 'active' : ''}`}>
                <i className="nav-icon fa-solid fa-gauge-simple-high"></i>
                <p>Dashboard</p>
              </Link>
            </li>


            <li className="nav-item">
              <Link to="/users" className={`nav-link ${pathname.startsWith('/users') || pathname.startsWith('/usersedit') || pathname.startsWith('/inviteUser') ? 'active' : ''}`}>
                <i className="nav-icon fa-solid fa-users"></i>
                <p>Users</p>
              </Link>
            </li>

            {/* Sign-up monitoring + referral program — CircleOne-only
                (requireNonMmlAdmin on /admin/referrals), gated like the
                other CircleOne-only screens. */}
            {showSettings && (
              <li className="nav-item">
                <Link to="/referrals" className={`nav-link ${pathname.startsWith('/referrals') ? 'active' : ''}`}>
                  <i className="nav-icon fa-solid fa-user-plus"></i>
                  <p>Sign-ups &amp; Referrals</p>
                </Link>
              </li>
            )}

            <li className={`nav-item has-treeview ${loyaltyOpen ? 'menu-open' : ''}`}>
              <div
                className={`nav-link ${isLoyaltyPath ? 'active' : ''}`}
                onClick={toggleLoyalty}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => e.currentTarget.classList.add('hover-mmi')}
                onMouseLeave={(e) => e.currentTarget.classList.remove('hover-mmi')}
              >
                <i className="nav-icon fa-solid fa-award"></i>
                <p>
                  Loyalty
                  <i className="right fas fa-angle-left"></i>
                </p>
              </div>

              <ul className="nav nav-treeview">
                <li className="nav-item">
                  <Link to="/loyaltyReceipts" className={`nav-link ${pathname === '/loyaltyReceipts' ? 'active' : ''}`}>
                    <i className="far fa-circle nav-icon"></i>
                    <p>Loyalty Receipts</p>
                  </Link>
                </li>

                {/* Totals across receipts (somo-circle1-api-admin#188) —
                    requireNonMmlAdmin on the backend, like the line items
                    it summarizes, so gated the same way as Upload. */}
                {showSettings && (
                  <li className="nav-item">
                    <Link to="/loyaltyInsights" className={`nav-link ${pathname.startsWith('/loyaltyInsights') ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Receipt Insights</p>
                    </Link>
                  </li>
                )}

                {/* Upload is CircleOne-only, same requireNonMmlAdmin gate as
                    Redemptions/Program Rules below (somo-circle1-api#662) —
                    the backend route 403s for an MML-sourced admin, so the
                    link is hidden the same way rather than showing a dead
                    end. */}
                {showSettings && (
                  <li className="nav-item">
                    <Link to="/loyaltyReceipts/upload" className={`nav-link ${pathname.startsWith('/loyaltyReceipts/upload') ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Upload Receipt</p>
                    </Link>
                  </li>
                )}

                {/* Held receipts from senders with no account
                    (somo-circle1-api#691) — requireNonMmlAdmin on the
                    backend, so gated the same way as Upload. */}
                {showSettings && (
                  <li className="nav-item">
                    <Link to="/loyaltyReceipts/unclaimed" className={`nav-link ${pathname.startsWith('/loyaltyReceipts/unclaimed') ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Unclaimed Receipts</p>
                    </Link>
                  </li>
                )}

                {/* Redemptions and Program Rules are both CircleOne-only
                    money-affecting screens (requireNonMmlAdmin on the
                    backend routes they call) — gated the same as the
                    Settings/Campaigns menus below, so an MML-sourced admin
                    never sees a link that just 403s. Receipts stays
                    ungated: its GET /admin/loyalty/receipts route isn't
                    restricted, only the approve/reject/config actions are. */}
                {showSettings && (
                  <li className="nav-item">
                    <Link to="/loyaltyRedemptions" className={`nav-link ${pathname.startsWith('/loyaltyRedemptions') ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Loyalty Redemptions</p>
                    </Link>
                  </li>
                )}

                {showSettings && (
                  <li className="nav-item">
                    <Link to="/loyaltyUnlocks" className={`nav-link ${pathname.startsWith('/loyaltyUnlocks') ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Membership Unlocks</p>
                    </Link>
                  </li>
                )}

                {showSettings && (
                  <li className="nav-item">
                    <Link to="/loyaltyRules" className={`nav-link ${pathname.startsWith('/loyaltyRules') ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Loyalty Program Rules</p>
                    </Link>
                  </li>
                )}
              </ul>
            </li>


            {showSettings && (
              <li className={`nav-item has-treeview ${settingsOpen ? 'menu-open' : ''}`}>
                <div
                  className={`nav-link ${isSettingsPath ? 'active' : ''}`}
                  onClick={toggleSettings}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => e.currentTarget.classList.add('hover-mmi')}
                  onMouseLeave={(e) => e.currentTarget.classList.remove('hover-mmi')}
                >
                  <i className="nav-icon fa-solid fa-gear"></i>
                  <p>
                    Settings
                    <i className="right fas fa-angle-left"></i>
                  </p>
                </div>

                <ul className="nav nav-treeview">
                  <li className="nav-item">
                    <Link to="/programFlags" className={`nav-link ${pathname.startsWith('/programFlags') ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Program Flags</p>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/dropdownOptions" className={`nav-link ${pathname.startsWith('/dropdownOptions') ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Dropdown Options</p>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/oidcClients" className={`nav-link ${pathname.startsWith('/oidcClients') ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Partner Clients</p>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/partnerInquiries" className={`nav-link ${pathname.startsWith('/partnerInquiries') ? 'active' : ''}`}>
                      <i className="far fa-circle nav-icon"></i>
                      <p>Partner Inquiries</p>
                    </Link>
                  </li>
                </ul>
              </li>
            )}

            {showSettings && (
              <li className={`nav-item has-treeview ${quizOpen ? 'menu-open' : ''}`}>
                <div
                  className={`nav-link ${isQuizPath ? 'active' : ''}`}
                  onClick={toggleQuiz}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => e.currentTarget.classList.add('hover-mmi')}
                  onMouseLeave={(e) => e.currentTarget.classList.remove('hover-mmi')}
                >
                  <i className="nav-icon fa-solid fa-clipboard-question"></i>
                  <p>
                    Campaigns
                    <i className="right fas fa-angle-left"></i>
                  </p>
                </div>

                <ul className="nav nav-treeview" style={{ paddingLeft: '1rem' }}>
                  <li className={`nav-item has-treeview ${quizSubOpen ? 'menu-open' : ''}`}>
                    <div
                      className={`nav-link ${isQuizPath ? 'active' : ''}`}
                      onClick={toggleQuizSub}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="nav-icon fa-solid fa-list-check"></i>
                      <p>
                        Quiz
                        <i className="right fas fa-angle-left"></i>
                      </p>
                    </div>

                    <ul className="nav nav-treeview" style={{ paddingLeft: '1rem' }}>
                      <li className="nav-item">
                        <Link to="/quizQuestions" className={`nav-link ${pathname.startsWith('/quizQuestions') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon"></i>
                          <p>Questions</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link to="/quizReportRules" className={`nav-link ${pathname.startsWith('/quizReportRules') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon"></i>
                          <p>Report Rules</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link to="/quizLeads" className={`nav-link ${pathname.startsWith('/quizLeads') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon"></i>
                          <p>Leads</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link to="/vaBenefits" className={`nav-link ${pathname.startsWith('/vaBenefits') ? 'active' : ''}`}>
                          <i className="far fa-circle nav-icon"></i>
                          <p>VA Benefits</p>
                        </Link>
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            )}

          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;

